import { Component, Signal, ChangeDetectionStrategy, ElementRef, input, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MetaConfigComponent } from '../meta-config/meta-config.component';
import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { Result, WebElement } from '../../../../state-management/model/interfaces/state.model';
import { ElementsType, StyleType } from '../../../../state-management/model/enums/state.enums';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EditableTextWarningDialogComponent } from './editable-text-warning-dialog/editable-text-warning-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { ClickOutsideDirective } from '../../../../directives/click-outside.directive';

@Component({
  selector: 'app-editable-text',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MetaConfigComponent,
    MatDialogModule,
    MatButtonModule,
    ClickOutsideDirective,
  ],
  templateUrl: './editable-text.component.html',
  styleUrl: './editable-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditableTextComponent {
  readonly textElementRef = viewChild.required<ElementRef>('textElement');

  readonly elemId = input.required<string>();
  readonly customClass = input<string | null>(null);

  public eType = ElementsType.Text;

  public isEditMode$: Signal<boolean> = this.stateService.isEditMode;
  public currentElement$!: Signal<Result<WebElement, string>>;
  public styles$!: Signal<Partial<Record<StyleType, string>>>;

  public metaConfigOpen: boolean = false;
  public showEditButton: boolean = false;
  public warningMessage: string = ''; // Nachricht für Warnung

  private lastValidValue: string = ''; // Speichert den letzten gültigen Wert
  private currentElementValue = ''; // Aktueller Wert des Texts (gebunden an die Komponente)

  ngOnInit() {
    this.currentElement$ = this.stateService.getCurrentElement(this.elemId());
    this.styles$ = this.stateService.getStylesForElement(this.elemId());

    const currentElement = this.currentElement$();
    this.currentElementValue = currentElement.success && currentElement.payload.value !== null ? currentElement.payload.value : '';
    this.lastValidValue = this.currentElementValue;
  }

  public onClickOutside(): void {
    this.closeMetaConfig();
  }

  public onEdit(): void {
    if (this.isEditMode$()) {
      this.showEditButton = true;
    }
  }

  public openConfig(event: Event) {
    event.stopPropagation(); // Verhindert, dass der Klick das Feld erneut fokussiert
    this.metaConfigOpen = true;
  }

  public closeMetaConfig(): void {
    this.metaConfigOpen = false;
    this.showEditButton = false;
  }

  public onInput(event: Event) {
    const input = (event.target as HTMLElement).innerText.trim();

    const currentElement = this.currentElement$();
    const tag = currentElement.success ? currentElement.payload.tag as keyof typeof tagLimits : 'p';

    const tagLimits = {
      "h1": { maxLength: 50, maxWords: 10 },
      "h2": { maxLength: 150, maxWords: 30 },
      "h3": { maxLength: 250, maxWords: 50 },
      "p": { maxLength: 500, maxWords: 150 },
      "span": { maxLength: 500, maxWords: 150 },
    };

    const maxLength = tagLimits[tag]?.maxLength || 300;
    const maxWords = tagLimits[tag]?.maxWords || 60;

    const wordCount = input.split(/\s+/).length; // Zähle die Wörter im Input

    if (input.length === 0) {
      this.warningMessage = 'Der Text darf nicht vollständig gelöscht werden!';
      this.openWarningDialog();
      // setTimeout(() => this.clearWarning(), 3000); // Entfernt die Warnung nach 3 Sekunden
      (event.target as HTMLElement).innerText = this.lastValidValue; // Setzt den Text zurück
    } else if (input.length > maxLength) {
      this.warningMessage = `Der Text darf nicht länger als ${maxLength} Zeichen sein.`;
      this.openWarningDialog();
      (event.target as HTMLElement).innerText = this.lastValidValue;
    } else if (wordCount > maxWords) {
      this.warningMessage = `Der Text darf nicht mehr als ${maxWords} Wörter enthalten.`;
      this.openWarningDialog();
      (event.target as HTMLElement).innerText = this.lastValidValue;
    } else {
      this.lastValidValue = input; // Speichere den gültigen Wert
    }
  }

  public onBlur(_event: Event) {
    console.log('onBlur:', this.currentElementValue, this.lastValidValue);
    if (this.lastValidValue !== this.currentElementValue) {
      this.currentElementValue = this.lastValidValue;
      this.stateService.updateElement(this.elemId(), { value: this.lastValidValue });
    }
  }


  private openWarningDialog() {
    this.dialog.open(EditableTextWarningDialogComponent, {
      data: { message: this.warningMessage },
      panelClass: 'admin-theme'
    });
  }

  constructor(
    private dialog: MatDialog,
    private stateService: WebsiteStateService
  ) { }
}